import type { ProductInfo } from "@expert/contentful";
import { LocaleCode } from "contentful";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import type { EligibilityMeta } from "@/sales-tools";
import type { EligibilityStatus, SalesProvider } from "../types";
import { SaleProgress } from "../types";
import { defaultEligibilityMeta, normalizeEligibilityMeta } from "./useProductOfferStore.helpers";

export interface EligibleProduct {
    id: string;
    productSku: string;
    status: EligibilityStatus;
    saleProgress: SaleProgress;
    isPrimaryProduct: boolean;
    confirmationCodePattern?: string;
    needsAddress?: string[] | undefined;
    isProductAsurionBilled?: boolean;
    requiresNyDisclosures?: boolean;
    isConfettiShown?: boolean;
    salesProvider: SalesProvider;
    productInfo?: ProductInfo | Record<LocaleCode, ProductInfo>;
}

type ProductState = {
    products: EligibleProduct[];
    selectedProduct: EligibleProduct | undefined;
};

export type SmartLinkMeta = {
    maximumSmartLinksGenerated: boolean;
};

export type SmartLinkState = Record<string, SmartLinkMeta>;

interface ProductOfferStore {
    productStateBySession: Record<string, ProductState>;
    eligibilityMetaBySession: Record<string, EligibilityMeta>;
    productInfoBySession: Record<string, Record<string, ProductInfo> | undefined>;
    smartLinkMetaBySession: Record<string, SmartLinkState>;

    getProductState: (sessionId: string) => ProductState;
    getProducts: (sessionId: string) => EligibleProduct[];
    getPrimaryProducts: (sessionId: string) => EligibleProduct[];
    getOtherProducts: (sessionId: string) => EligibleProduct[];
    getSelectedProduct: (sessionId: string) => EligibleProduct | undefined;
    getSessionIdForProductId: (productId: string) => string | undefined;
    getEligibilityMetaBySessionId: (sessionId: string) => EligibilityMeta;
    getSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string) => SmartLinkMeta;

    updateProducts: (sessionId: string, product: EligibleProduct[]) => void;
    updateProduct: (
        sessionId: string,
        productId: string,
        change: { saleProgress?: SaleProgress; status?: EligibilityStatus; isConfettiShown?: boolean },
    ) => void;
    updateProductsConfettiStatus: (sessionId: string) => void;
    updateSelectedProduct: (sessionId: string, product: EligibleProduct | undefined) => void;
    updateEligibilityMetaBySessionId: (
        sessionId: string,
        eligibilityMeta: EligibilityMeta | Partial<EligibilityMeta>,
    ) => void;
    updateSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string, smartLinkMeta: SmartLinkMeta) => void;

    isPrimaryProductInProgress: (sessionId: string) => boolean;
    updateProductInfo: (sessionId: string, productInfo?: Record<string, ProductInfo>) => void;
    getProductInfo: (sessionId: string) => Record<string, ProductInfo> | undefined;
    resetSaleSession: (sessionId: string) => void;
}

export const useProductOfferStore = createWithEqualityFn<ProductOfferStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    productStateBySession: {},
                    eligibilityMetaBySession: {},
                    productInfoBySession: {},
                    smartLinkMetaBySession: {},
                    updateProducts: (sessionId, products) => {
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const existingProducts = currentProductState.products;

                                // try to maintain sales progress state of the current products when setting new products
                                const updatedProducts = products.map((newProduct) => {
                                    // find matching product from new state in the existing state
                                    const matchingExistingProduct = existingProducts.find(
                                        (existingProduct) => existingProduct.id === newProduct.id,
                                    );

                                    // if no match, do not alter the incoming product
                                    if (!matchingExistingProduct) {
                                        return newProduct;
                                    }

                                    // if match, maintain the existing state sale progress
                                    return {
                                        ...newProduct,
                                        saleProgress: matchingExistingProduct.saleProgress,
                                    };
                                });

                                state.productStateBySession[sessionId] = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                };
                            },
                            false,
                            "updateProducts",
                        );
                    },
                    updateProduct: (sessionId, productId, change) => {
                        set(
                            (state) => {
                                const { products, selectedProduct } = state.getProductState(sessionId);
                                if (!products) {
                                    return;
                                }

                                const newPrimaryProducts = products.map((currentProduct) =>
                                    currentProduct.id === productId ? { ...currentProduct, ...change } : currentProduct,
                                );

                                const newSelectedProduct =
                                    selectedProduct?.id === productId
                                        ? { ...selectedProduct, ...change }
                                        : selectedProduct;

                                state.productStateBySession[sessionId] = {
                                    ...state.productStateBySession[sessionId],
                                    products: newPrimaryProducts,
                                    selectedProduct: newSelectedProduct,
                                };
                            },
                            false,
                            "updateProduct",
                        );
                    },
                    updateProductsConfettiStatus: (sessionId) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const updatedProducts = currentProductState.products.map((product) => {
                                    if (product.saleProgress === SaleProgress.Success && !product.isConfettiShown) {
                                        return { ...product, isConfettiShown: true };
                                    }
                                    return product;
                                });
                                state.productStateBySession[sessionId] = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                };
                            },
                            false,
                            "updateProductsConfettiStatus",
                        ),
                    updateSelectedProduct: (sessionId, product) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                state.productStateBySession[sessionId] = {
                                    ...currentProductState,
                                    selectedProduct: product,
                                };
                            },
                            false,
                            "updateSelectedProduct",
                        ),
                    updateEligibilityMetaBySessionId: (sessionId, eligibilityMeta) => {
                        set(
                            (state) => {
                                state.eligibilityMetaBySession[sessionId] = normalizeEligibilityMeta(eligibilityMeta);
                            },
                            false,
                            "updateEligibilityMeta",
                        );
                    },

                    updateProductInfo: (sessionId, productInfo) => {
                        set(
                            (state) => {
                                state.productInfoBySession[sessionId] = productInfo;
                            },
                            false,
                            "updateProductInfo",
                        );
                    },
                    updateSmartLinkMetaBySessionId: (sessionId, productOfferId, smartLinkMeta) => {
                        set(
                            (state) => {
                                if (!state.smartLinkMetaBySession[sessionId]) {
                                    state.smartLinkMetaBySession[sessionId] = {};
                                }
                                state.smartLinkMetaBySession[sessionId][productOfferId] = smartLinkMeta;
                            },
                            false,
                            "updateSmartLinkMeta",
                        );
                    },
                    isPrimaryProductInProgress: (sessionId) => {
                        return (
                            get()
                                .getPrimaryProducts(sessionId)
                                ?.some((p) => p.saleProgress === SaleProgress.InProgress) ?? false
                        );
                    },
                    getProductState: (sessionId) => {
                        return (
                            get().productStateBySession[sessionId] ?? {
                                products: [],
                                selectedProduct: undefined,
                            }
                        );
                    },
                    getProducts: (sessionId) => {
                        return get().getProductState(sessionId).products;
                    },
                    getPrimaryProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => p.isPrimaryProduct) ?? [],
                    getOtherProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => !p.isPrimaryProduct) ?? [],
                    getSelectedProduct: (sessionId) => {
                        return get().getProductState(sessionId).selectedProduct;
                    },
                    getSessionIdForProductId: (productId) => {
                        return Object.keys(get().productStateBySession).find((sessionId) =>
                            get()
                                .getProductState(sessionId)
                                .products.some((product) => product.id === productId),
                        );
                    },
                    getEligibilityMetaBySessionId: (sessionId) => {
                        return get().eligibilityMetaBySession[sessionId] ?? { ...defaultEligibilityMeta };
                    },
                    getProductInfo: (sessionId) => {
                        return get().productInfoBySession[sessionId];
                    },

                    getSmartLinkMetaBySessionId: (sessionId, productOfferId) => {
                        const sessionMeta = get().smartLinkMetaBySession[sessionId];
                        if (!sessionMeta) {
                            return { maximumSmartLinksGenerated: false };
                        }
                        return sessionMeta[productOfferId] ?? { maximumSmartLinksGenerated: false };
                    },
                    resetSaleSession: (sessionId) =>
                        set(
                            (state) => {
                                delete state.productStateBySession[sessionId];
                                delete state.productInfoBySession[sessionId];
                            },
                            false,
                            "resetSaleSession",
                        ),
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "product-offer-store",
                    name: "workspace/sales-tools",
                },
            ),
        ),
        {
            name: "product-offer-store",
            // Skipped, but manually called elsewhere: void useProductOfferStore.persist.rehydrate();
            skipHydration: true,
        },
    ),
    shallow,
);
